import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { ShoppingCart, Pencil, RefreshCw, Loader2, TrendingUp, Lock } from "lucide-react";

const usd = (n, max = 2) =>
  isFinite(n)
    ? n.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: Math.min(2, max), maximumFractionDigits: max })
    : "—";

const num = (n, digits = 2) =>
  isFinite(n) ? n.toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits }) : "—";

// Net Purchase Price per Kilo calculation
const computeRecommendation = (goldPricePerOz, s) => {
  const f = Number(s.conversion_factor) || 0;
  const purity = (Number(s.regional_purity_pct) || 0) / 100;
  const discount = Number(s.vendor_discount_per_gram) || 0;
  const discountMode = s.vendor_discount_mode === "%" ? "%" : "$";
  const kilos = Number(s.total_weight_kilos) || 0;
  const transportPer100 = Number(s.transport_cost_per_100kg) || 0;

  const pricePerGram = f > 0 ? (goldPricePerOz / f) * purity : 0; // oz -> gram, apply purity
  const netPerGram =
    discountMode === "%"
      ? pricePerGram * (1 - discount / 100) // percent discount off per-gram price
      : pricePerGram - discount;             // flat dollars per gram
  const goldPricePerKilo = netPerGram * 1000; // grams -> kilo

  const billableKilos = Math.max(kilos, 100); // minimum 100-kilo transport billing
  const transportTotal = (billableKilos / 100) * transportPer100;
  const transportPerKilo = kilos > 0 ? transportTotal / kilos : 0;

  const netPurchasePricePerKilo = goldPricePerKilo + transportPerKilo; // transport added
  const totalLotCost = goldPricePerKilo * kilos + transportTotal;

  return { pricePerGram, netPerGram, goldPricePerKilo, transportTotal, transportPerKilo, netPurchasePricePerKilo, totalLotCost };
};

const FIELD_DEFS = [
  { key: "regional_purity_pct", label: "Regional Gold Purity (%)", type: "number", step: "0.01", suffix: "%" },
  { key: "vendor_description", label: "Vendor (discount source)", type: "text" },
  {
    key: "vendor_discount_per_gram",
    label: "Vendor Discount per Gram",
    type: "number",
    step: "0.01",
    hasDiscountModeToggle: true, // toggle between "$" and "%" mode
  },
  { key: "total_weight_kilos", label: "Total Lot Weight (kilos)", type: "number", step: "1", suffix: "kg" },
  { key: "transport_cost_per_100kg", label: "Inbound Transport Cost (per 100 kg, $)", type: "number", step: "100", prefix: "$", adminOnly: true },
  { key: "foreign_investment_injection", label: "Foreign Investment Injection ($)", type: "number", step: "100000", prefix: "$", adminViewOnly: true },
];

const GoldPurchaseRecommendationPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [locking, setLocking] = useState(false);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/gold-arbitrage/purchase`, { withCredentials: true });
      setData(res.data);
    } catch (e) {
      toast.error("Failed to load recommendation data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleLock = async () => {
    setLocking(true);
    try {
      const res = await axios.post(`${API}/gold-arbitrage/purchase/lock`, {}, { withCredentials: true });
      toast.success(`Lot ${res.data.lot_id} locked in — view it on the Gold Sales Recommendation page`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to lock in lot");
    } finally {
      setLocking(false);
    }
  };


  const settings = data?.settings || {};
  const goldPrice = data?.gold_price_per_oz || 0;
  const goldPriceSource = data?.gold_price_source || settings.gold_price_source || "LMI";

  // Effective gold price to use during PREVIEW inside the edit dialog:
  // if the user has toggled to Kitco AND entered a manual value, use that; else fall back to live.
  const previewGoldPrice = useMemo(() => {
    if (form.gold_price_source === "Kitco") {
      const manual = Number(form.gold_price_kitco_manual);
      if (isFinite(manual) && manual > 0) return manual;
    }
    return goldPrice;
  }, [form, goldPrice]);

  const calc = useMemo(() => computeRecommendation(goldPrice, settings), [goldPrice, settings]);
  const previewCalc = useMemo(() => computeRecommendation(previewGoldPrice, form), [previewGoldPrice, form]);

  const openEdit = () => {
    setForm({
      ...settings,
      // Ensure toggle fields have safe defaults
      gold_price_source: settings.gold_price_source || "LMI",
      gold_price_kitco_manual: settings.gold_price_kitco_manual ?? "",
    });
    setEditOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        regional_purity_pct: Number(form.regional_purity_pct),
        vendor_description: form.vendor_description,
        vendor_discount_per_gram: Number(form.vendor_discount_per_gram),
        vendor_discount_mode: form.vendor_discount_mode === "%" ? "%" : "$",
        total_weight_kilos: Number(form.total_weight_kilos),
        gold_price_source: form.gold_price_source === "Kitco" ? "Kitco" : "LMI",
        gold_price_kitco_manual: Number(form.gold_price_kitco_manual) || 0,
      };
      // Transport cost & foreign investment are admin-only; only include when an admin edits
      if (isAdmin) {
        payload.transport_cost_per_100kg = Number(form.transport_cost_per_100kg);
        payload.foreign_investment_injection = Number(form.foreign_investment_injection);
      }
      const res = await axios.patch(`${API}/gold-arbitrage/purchase`, payload, { withCredentials: true });
      setData(res.data);
      setEditOpen(false);
      toast.success("Variables updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const rows = [
    {
      label: `${goldPriceSource === "Kitco" ? "Kitco" : "LMI"} — ${goldPriceSource === "Kitco" ? "Manual" : "London Market Index"} Gold Price / Ounce`,
      sub: goldPriceSource === "Kitco" ? "Manually entered price" : "Live Yahoo Finance gold, at 99% pure",
      value: usd(goldPrice),
      badge: goldPriceSource === "Kitco" ? "Kitco" : "Live",
    },
    {
      label: "Standard Conversion Factor",
      sub: "Troy ounce → grams",
      value: num(settings.conversion_factor, 4),
    },
    {
      label: "Regional Gold Purity",
      sub: "Purity of gold mined in the region",
      value: `${num(settings.regional_purity_pct, 2)}%`,
    },
    {
      label: "Vendor Discount per Gram",
      sub: settings.vendor_description ? `${settings.vendor_description} vendor` : "Vendor",
      value:
        (settings.vendor_discount_mode === "%")
          ? `${num(settings.vendor_discount_per_gram, 2)}% / g`
          : `${usd(settings.vendor_discount_per_gram)} / g`,
    },
    {
      label: "Total Lot Weight",
      sub: "Kilos of gold to be purchased",
      value: `${num(settings.total_weight_kilos, 0)} kg`,
    },
    {
      label: "Inbound Transport / Logistics",
      sub: "Kalemie, DRC → Bujumbura, Burundi",
      value: `${usd(settings.transport_cost_per_100kg)} / 100 kg`,
    },
  ];

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto space-y-8" data-testid="gold-purchase-recommendation-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-12 h-12 rounded-sm bg-primary/10 text-primary">
              <ShoppingCart className="w-6 h-6" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-primary">Govt. Gold Arbitrage</p>
              <h1 className="text-3xl font-bold text-foreground">GEOCORE Gold Purchase Speculation</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={fetchData}
              title="Refresh live price"
              data-testid="gold-purchase-refresh-btn"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button onClick={openEdit} className="gap-2" data-testid="gold-purchase-edit-btn">
              <Pencil className="w-4 h-4" />
              Edit Variables
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-muted-foreground">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : (
          <>
            {/* Variables table */}
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="text-base">Variable Values</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-border" data-testid="gold-purchase-variables-table">
                  {rows.map((row, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between gap-4 px-6 py-2"
                      data-testid={`gold-purchase-row-${i}`}
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-foreground">{row.label}</span>
                          {row.badge && (
                            <Badge className="bg-emerald-500/15 text-emerald-400 border-0 text-[10px] uppercase tracking-wide">
                              {row.badge}
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">{row.sub}</p>
                      </div>
                      <span className="text-sm font-semibold text-foreground font-mono whitespace-nowrap">
                        {row.value}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Result */}
            <Card className="border-primary/30 bg-primary/5" data-testid="gold-purchase-result-card">
              <CardContent className="py-6">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-primary">
                      Net Purchase Price per Kilo of Gold
                    </p>
                    <p
                      className="text-4xl font-bold text-foreground font-mono mt-1"
                      data-testid="gold-purchase-net-price"
                    >
                      {usd(calc.netPurchasePricePerKilo)}
                    </p>
                  </div>
                  <div className="text-right text-sm text-muted-foreground space-y-1">
                    <div className="flex items-center justify-end gap-2">
                      <span>Gold value / kg</span>
                      <span className="font-mono text-foreground">{usd(calc.goldPricePerKilo)}</span>
                    </div>
                    <div className="flex items-center justify-end gap-2">
                      <span>+ Transport / kg</span>
                      <span className="font-mono text-foreground">{usd(calc.transportPerKilo)}</span>
                    </div>
                  </div>
                </div>
                <Separator className="my-4" />
                <div className="flex items-end justify-between gap-6 flex-wrap">
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-xs text-muted-foreground">Price / gram (post-discount)</p>
                      <p className="font-mono text-foreground">{usd(calc.netPerGram)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Transport total (lot)</p>
                      <p className="font-mono text-foreground">{usd(calc.transportTotal)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Lot weight</p>
                      <p className="font-mono text-foreground">{num(settings.total_weight_kilos, 0)} kg</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Total lot cost
                    </p>
                    <p
                      className="text-4xl font-bold text-foreground font-mono mt-1"
                      data-testid="gold-purchase-total-lot-cost"
                    >
                      {usd(calc.totalLotCost, 0)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {data?.updated_by && (
              <p className="text-xs text-muted-foreground">
                Last edited by {data.updated_by}
                {data.updated_at ? ` on ${new Date(data.updated_at).toLocaleString()}` : ""}
              </p>
            )}

            {/* Lock in button (bottom right) */}
            <div className="flex justify-end">
              <Button
                size="lg"
                onClick={handleLock}
                disabled={locking}
                className="gap-2"
                data-testid="gold-purchase-lock-btn"
              >
                {locking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                Lock in Prices and Purchase
              </Button>
            </div>
          </>
        )}

        {/* Edit modal */}
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="sm:max-w-lg" data-testid="gold-purchase-edit-dialog">
            <DialogHeader>
              <DialogTitle>Edit Purchase Variables</DialogTitle>
              <DialogDescription>
                These values are shared globally. Choose your gold price source below (LMI live feed or manually-entered Kitco).
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
              {/* Gold Price section with LMI / Kitco toggle */}
              <div className="space-y-1.5">
                <Label className="text-muted-foreground">Gold Price / Ounce</Label>
                <div className="flex items-stretch gap-2">
                  {/* LMI / Kitco toggle */}
                  <div
                    className="inline-flex items-center rounded-sm border border-border overflow-hidden shrink-0"
                    role="group"
                    data-testid="gold-purchase-price-source-toggle"
                  >
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, gold_price_source: "LMI" })}
                      className={`px-3 py-2 text-sm font-semibold transition-colors ${
                        (form.gold_price_source || "LMI") === "LMI"
                          ? "bg-primary text-primary-foreground"
                          : "bg-secondary/50 text-muted-foreground hover:text-foreground"
                      }`}
                      data-testid="gold-purchase-price-source-lmi"
                    >
                      LMI
                    </button>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, gold_price_source: "Kitco" })}
                      className={`px-3 py-2 text-sm font-semibold transition-colors border-l border-border ${
                        form.gold_price_source === "Kitco"
                          ? "bg-primary text-primary-foreground"
                          : "bg-secondary/50 text-muted-foreground hover:text-foreground"
                      }`}
                      data-testid="gold-purchase-price-source-kitco"
                    >
                      Kitco
                    </button>
                  </div>
                  {/* Value: LMI = live read-only; Kitco = manual editable input */}
                  {form.gold_price_source === "Kitco" ? (
                    <div className="relative flex-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Enter Kitco price"
                        value={form.gold_price_kitco_manual ?? ""}
                        onChange={(e) => setForm({ ...form, gold_price_kitco_manual: e.target.value })}
                        className="pl-7 pr-14"
                        data-testid="gold-purchase-price-kitco-input"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">
                        / oz
                      </span>
                    </div>
                  ) : (
                    <div className="flex flex-1 items-center gap-2 rounded-sm border border-border bg-secondary/50 px-3 py-2 text-sm font-mono">
                      <TrendingUp className="w-4 h-4 text-emerald-400" />
                      <span>{usd(goldPrice)}</span>
                      <span className="text-xs text-muted-foreground ml-auto">live · read-only</span>
                    </div>
                  )}
                </div>
                <p className="text-xs text-muted-foreground pt-1">
                  {form.gold_price_source === "Kitco"
                    ? "Manual mode — all calculations use the value you enter here."
                    : "LMI mode — Yahoo Finance live gold price, refreshed every 15 minutes."}
                </p>
              </div>

              {/* Read-only conversion factor (fixed constant, never adjusted) */}
              <div className="space-y-1.5">
                <Label className="text-muted-foreground">Standard Conversion Factor (oz → grams, read-only)</Label>
                <div className="flex items-center rounded-sm border border-border bg-secondary/50 px-3 py-2 text-sm font-mono text-muted-foreground">
                  {num(settings.conversion_factor, 4)}
                </div>
              </div>

              {FIELD_DEFS.map((f) => {
                if (f.adminViewOnly && !isAdmin) return null;
                const locked = f.adminOnly && !isAdmin;
                const mode = form.vendor_discount_mode === "%" ? "%" : "$";
                return (
                  <div className="space-y-1.5" key={f.key}>
                    <Label htmlFor={`field-${f.key}`}>
                      {f.label}
                      {locked && <span className="ml-2 text-xs text-muted-foreground">(admin only)</span>}
                    </Label>
                    {locked ? (
                      <div
                        className="flex items-center rounded-sm border border-border bg-secondary/50 px-3 py-2 text-sm font-mono text-muted-foreground"
                        data-testid={`gold-purchase-readonly-${f.key}`}
                      >
                        {f.prefix}{form[f.key] ?? ""}{f.suffix ? ` ${f.suffix}` : ""}
                      </div>
                    ) : f.hasDiscountModeToggle ? (
                      <div className="flex items-stretch gap-2">
                        {/* $ / % toggle */}
                        <div
                          className="inline-flex items-center rounded-sm border border-border overflow-hidden shrink-0"
                          role="group"
                          data-testid="gold-purchase-discount-mode-toggle"
                        >
                          <button
                            type="button"
                            onClick={() => setForm({ ...form, vendor_discount_mode: "$" })}
                            className={`px-3 py-2 text-sm font-semibold transition-colors ${
                              mode === "$"
                                ? "bg-primary text-primary-foreground"
                                : "bg-secondary/50 text-muted-foreground hover:text-foreground"
                            }`}
                            data-testid="gold-purchase-discount-mode-dollar"
                          >
                            $
                          </button>
                          <button
                            type="button"
                            onClick={() => setForm({ ...form, vendor_discount_mode: "%" })}
                            className={`px-3 py-2 text-sm font-semibold transition-colors border-l border-border ${
                              mode === "%"
                                ? "bg-primary text-primary-foreground"
                                : "bg-secondary/50 text-muted-foreground hover:text-foreground"
                            }`}
                            data-testid="gold-purchase-discount-mode-percent"
                          >
                            %
                          </button>
                        </div>
                        {/* Value input with adaptive prefix/suffix */}
                        <div className="relative flex-1">
                          {mode === "$" && (
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                              $
                            </span>
                          )}
                          <Input
                            id={`field-${f.key}`}
                            type={f.type}
                            step={f.step}
                            value={form[f.key] ?? ""}
                            onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                            className={mode === "$" ? "pl-7 pr-14" : "pr-16"}
                            data-testid={`gold-purchase-input-${f.key}`}
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">
                            {mode === "%" ? "% / g" : "/ gram"}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="relative">
                        {f.prefix && (
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                            {f.prefix}
                          </span>
                        )}
                        <Input
                          id={`field-${f.key}`}
                          type={f.type}
                          step={f.step}
                          value={form[f.key] ?? ""}
                          onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                          className={f.prefix ? "pl-7" : ""}
                          data-testid={`gold-purchase-input-${f.key}`}
                        />
                        {f.suffix && (
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                            {f.suffix}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Live preview */}
              <div className="rounded-sm border border-primary/30 bg-primary/5 px-4 py-3">
                <p className="text-xs font-semibold uppercase tracking-wider text-primary">
                  Net Purchase Price per Kilo (preview)
                </p>
                <p className="text-2xl font-bold font-mono text-foreground" data-testid="gold-purchase-preview-net-price">
                  {usd(previewCalc.netPurchasePricePerKilo)}
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditOpen(false)} data-testid="gold-purchase-cancel-btn">
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving} data-testid="gold-purchase-save-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Changes"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default GoldPurchaseRecommendationPage;
